import * as THREE from "three";
import { mergeGeometries } from "three/addons/utils/BufferGeometryUtils.js";
import { terrainHeightMeters } from "../core/TokyoTerrain";
import { SeededRandom } from "../core/random";

const HOUSE_WOOD = new THREE.Color(0xb77446);
const HOUSE_PLASTER = new THREE.Color(0xf7e8cf);
const HOUSE_TATAMI = new THREE.Color(0xaec47b);
const HOUSE_TRIM = new THREE.Color(0x334b50);
const GENKAN_STONE = new THREE.Color(0x71858a);

const ROAD = new THREE.Color(0x50636c);
const ROAD_MARK = new THREE.Color(0xfff4be);
const RIVER = new THREE.Color(0x62bce0);
const BRIDGE = new THREE.Color(0xd98268);
const TREE = new THREE.Color(0x5eae72);
const TRUNK = new THREE.Color(0x826245);
const BUILDING_COLORS = [
  new THREE.Color(0xe9e2d4),
  new THREE.Color(0xb8d5df),
  new THREE.Color(0xe7bdad),
  new THREE.Color(0xc9d3b0),
  new THREE.Color(0xd0c6dd),
] as const;

function colorGeometry(
  geometry: THREE.BufferGeometry,
  color: THREE.Color,
): THREE.BufferGeometry {
  const position = geometry.getAttribute("position");
  const colors = new Float32Array(position.count * 3);
  for (let index = 0; index < position.count; index += 1) {
    const offset = index * 3;
    colors[offset] = color.r;
    colors[offset + 1] = color.g;
    colors[offset + 2] = color.b;
  }
  geometry.setAttribute("color", new THREE.BufferAttribute(colors, 3));
  return geometry;
}

function box(
  width: number,
  height: number,
  depth: number,
  x: number,
  y: number,
  z: number,
  color: THREE.Color,
  yaw = 0,
): THREE.BufferGeometry {
  const geometry = new THREE.BoxGeometry(width, height, depth);
  if (yaw !== 0) {
    geometry.rotateY(yaw);
  }
  geometry.translate(x, y, z);
  return colorGeometry(geometry, color);
}

function mergeColored(
  parts: THREE.BufferGeometry[],
  name: string,
): THREE.BufferGeometry {
  const mergeParts = parts.map((part) => (part.index ? part.toNonIndexed() : part));
  const merged = mergeGeometries(mergeParts, false);
  for (const part of mergeParts) {
    if (!parts.includes(part)) {
      part.dispose();
    }
  }
  for (const part of parts) {
    part.dispose();
  }
  if (!merged) {
    throw new Error(`Failed to merge ${name}`);
  }
  merged.computeBoundingBox();
  merged.computeBoundingSphere();
  merged.name = name;
  return merged;
}

function createInitialHouseGeometry(): THREE.BufferGeometry {
  const parts: THREE.BufferGeometry[] = [];

  // Open-roof dollhouse proportions keep the starting room readable from the
  // gameplay camera while still giving the ball an unmistakable home interior.
  parts.push(box(7.6, 0.08, 5.9, 0, -0.04, 0.25, HOUSE_TATAMI));
  parts.push(box(7.6, 0.78, 0.12, 0, 0.39, 3.15, HOUSE_PLASTER));
  parts.push(box(0.12, 0.78, 5.9, -3.74, 0.39, 0.25, HOUSE_PLASTER));
  parts.push(box(0.12, 0.78, 5.9, 3.74, 0.39, 0.25, HOUSE_PLASTER));
  parts.push(box(2.65, 0.78, 0.12, -2.45, 0.39, -2.68, HOUSE_PLASTER));
  parts.push(box(2.65, 0.78, 0.12, 2.45, 0.39, -2.68, HOUSE_PLASTER));

  // Genkan, threshold and timber frame.
  parts.push(box(2.3, 0.08, 1.42, 0, -0.116, -3.38, GENKAN_STONE));
  parts.push(box(2.55, 0.08, 0.18, 0, -0.02, -2.62, HOUSE_WOOD));
  for (const x of [-3.68, -1.12, 1.12, 3.68]) {
    parts.push(box(0.1, 0.9, 0.1, x, 0.45, -2.67, HOUSE_TRIM));
  }
  parts.push(box(7.5, 0.1, 0.1, 0, 0.84, -2.67, HOUSE_TRIM));

  // A bounded selection of recognizable furniture, all folded into this draw.
  parts.push(box(1.45, 0.12, 0.92, -1.5, 0.42, 0.35, HOUSE_WOOD));
  for (const x of [-2.02, -0.98]) {
    for (const z of [0.02, 0.68]) {
      parts.push(box(0.1, 0.42, 0.1, x, 0.21, z, HOUSE_WOOD));
    }
  }
  parts.push(box(0.78, 0.52, 1.55, 2.55, 0.26, 1.55, HOUSE_TATAMI, -0.08));
  parts.push(box(0.22, 0.68, 1.82, 3.1, 0.34, 1.55, HOUSE_WOOD));
  parts.push(box(1.55, 0.08, 0.52, 0.9, 0.2, 2.52, HOUSE_WOOD));

  return mergeColored(parts, "tokyo-home-merged-geometry");
}

function createTokyoCityGeometry(): THREE.BufferGeometry {
  const parts: THREE.BufferGeometry[] = [];
  const random = new SeededRandom(0x74_6f_6b_79);

  // Residential road, lane marks, arterial roads and the Sumida-inspired river.
  parts.push(box(88, 0.035, 6.4, 0, -0.055, -7.4, ROAD));
  parts.push(box(0.12, 0.018, 88, 14, -0.045, 24, ROAD_MARK));
  parts.push(box(560, 0.08, 20, 0, -0.2, -82, ROAD));
  parts.push(box(18, 0.08, 560, 92, -0.2, -10, ROAD));
  parts.push(box(210, 0.06, 1_900, 450, -0.08, -180, RIVER));
  parts.push(box(248, 0.55, 18, 450, 0.12, -130, BRIDGE));
  for (let mark = -5; mark <= 5; mark += 1) {
    parts.push(box(18, 0.035, 0.36, mark * 42, -0.145, -82, ROAD_MARK));
  }

  // Near residential silhouettes make the home-to-street transition legible.
  for (let index = 0; index < 22; index += 1) {
    const side = index % 2 === 0 ? -1 : 1;
    const x = -72 + index * 7.2;
    const z = side < 0 ? -18 - random.range(0, 8) : 10 + random.range(0, 10);
    const width = random.range(4.2, 7.4);
    const depth = random.range(4.5, 8.5);
    const height = random.range(4.2, 9.5);
    const color = BUILDING_COLORS[index % BUILDING_COLORS.length] ?? BUILDING_COLORS[0];
    const ground = terrainHeightMeters(x, z, 1);
    parts.push(box(width, height, depth, x, ground + height * 0.5, z, color));
  }

  // Fixed-count procedural skyline. Physical placement spans the later scales,
  // but remains one immutable geometry and one submitted draw.
  for (let index = 0; index < 72; index += 1) {
    const angle = random.range(0, Math.PI * 2);
    const radius = random.range(170, 2_350);
    const x = Math.cos(angle) * radius + random.range(-65, 65);
    const z = Math.sin(angle) * radius + random.range(-65, 65);
    const footprint = random.range(18, 58) * (0.72 + radius / 3_800);
    const depth = footprint * random.range(0.7, 1.35);
    const height = random.range(32, 185) * (0.78 + radius / 4_700);
    const color = BUILDING_COLORS[index % BUILDING_COLORS.length] ?? BUILDING_COLORS[0];
    const ground = terrainHeightMeters(x, z, 4);
    parts.push(box(
      footprint,
      height,
      depth,
      x,
      ground + height * 0.5,
      z,
      color,
      random.range(-0.16, 0.16),
    ));
    if (index % 5 === 0) {
      parts.push(box(
        footprint * 0.34,
        height * 0.12,
        depth * 0.34,
        x,
        ground + height * 1.06,
        z,
        HOUSE_TRIM,
      ));
    }
  }

  // Reused low-poly street trees add a daytime Tokyo cue without another mesh.
  for (let index = 0; index < 14; index += 1) {
    const x = -38 + index * 5.6;
    const z = -12.4;
    const trunk = new THREE.CylinderGeometry(0.18, 0.23, 2.2, 5, 1);
    trunk.translate(x, 1.1, z);
    parts.push(colorGeometry(trunk, TRUNK));
    const crown = new THREE.IcosahedronGeometry(1.2, 0);
    crown.scale(1, 1.25, 1);
    crown.translate(x, 2.75, z);
    parts.push(colorGeometry(crown, TREE));
  }

  return mergeColored(parts, "tokyo-city-and-roads-merged-geometry");
}

export class TokyoScenery {
  readonly group = new THREE.Group();
  readonly house: THREE.Mesh;
  readonly city: THREE.Mesh;

  private readonly houseGeometry: THREE.BufferGeometry;
  private readonly cityGeometry: THREE.BufferGeometry;
  private readonly houseMaterial: THREE.MeshStandardMaterial;
  private readonly cityMaterial: THREE.MeshStandardMaterial;

  constructor() {
    this.group.name = "bounded-tokyo-scenery";
    this.houseGeometry = createInitialHouseGeometry();
    this.cityGeometry = createTokyoCityGeometry();
    this.houseMaterial = new THREE.MeshStandardMaterial({
      color: 0xffffff,
      vertexColors: true,
      roughness: 0.72,
      metalness: 0.01,
      flatShading: true,
    });
    this.houseMaterial.name = "tokyo-home-one-draw-material";
    this.cityMaterial = new THREE.MeshStandardMaterial({
      color: 0xffffff,
      vertexColors: true,
      roughness: 0.76,
      metalness: 0.025,
      flatShading: true,
      fog: true,
    });
    this.cityMaterial.name = "tokyo-city-one-draw-material";

    this.house = new THREE.Mesh(this.houseGeometry, this.houseMaterial);
    this.house.name = "initial-tokyo-home-single-draw";
    this.house.frustumCulled = false;
    this.city = new THREE.Mesh(this.cityGeometry, this.cityMaterial);
    this.city.name = "procedural-tokyo-scenery-single-draw";
    this.city.frustumCulled = false;
    this.group.add(this.city, this.house);
  }

  update(
    playerXMeters: number,
    playerZMeters: number,
    renderMetersPerUnit: number,
    tierIndex: number,
  ): void {
    const scale = Math.max(0.000_001, renderMetersPerUnit);
    const boundedTier = Math.max(0, Math.min(5, Math.floor(tierIndex)));
    const playerGround = terrainHeightMeters(playerXMeters, playerZMeters, boundedTier);
    this.group.position.set(
      -playerXMeters / scale,
      -playerGround / scale,
      -playerZMeters / scale,
    );
    this.group.scale.setScalar(1 / scale);
    this.house.visible = boundedTier === 0;
  }

  get visibleDrawCount(): number {
    return (this.city.visible ? 1 : 0) + (this.house.visible ? 1 : 0);
  }

  dispose(): void {
    this.group.remove(this.city, this.house);
    this.houseGeometry.dispose();
    this.cityGeometry.dispose();
    this.houseMaterial.dispose();
    this.cityMaterial.dispose();
  }
}
